import * as React from 'react';

import { cn } from '../../lib/utils';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * Names the horizontal scroll region and makes it keyboard reachable.
   *
   * The wrapper scrolls horizontally on narrow viewports, so without a
   * tabIndex a keyboard user cannot reach the columns that scrolled off
   * (WCAG 2.1.1). Without it, a table named by `aria-labelledby` still gets a
   * focusable region sharing that name — a raw Table outside DataTable must be
   * named one way or the other (DESIGN_SYSTEM.md §8.12). With neither, the
   * wrapper stays plain. DataTable does not use it: it names its region with
   * `listLabel` through `wrapperProps`, which still wins.
   */
  scrollRegionLabel?: string;
  /** Ref to the wrapper — DataTable's scroll region. */
  wrapperRef?: React.Ref<HTMLDivElement>;
  /** Replaces the wrapper's overflow classes. */
  wrapperClassName?: string;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement> & Record<`data-${string}`, string | number | boolean | undefined>;
  /** Rendered after `</table>`, inside the wrapper (DataTable's trailing controls). */
  trailing?: React.ReactNode;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, scrollRegionLabel, wrapperRef, wrapperClassName, wrapperProps, trailing, ...props }, ref) => {
    const labelledBy = props['aria-labelledby'];
    const region = scrollRegionLabel
      ? { tabIndex: 0, role: 'region' as const, 'aria-label': scrollRegionLabel }
      : labelledBy
        ? { tabIndex: 0, role: 'region' as const, 'aria-labelledby': labelledBy }
        : null;
    return (
      <div
        ref={wrapperRef}
        className={cn(
          wrapperClassName ?? 'relative w-full overflow-auto',
          // Inset: the wrapper's own overflow would clip an outside ring.
          region && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        )}
        {...(region ?? {})}
        {...wrapperProps}
      >
        <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
        {trailing}
      </div>
    );
  },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('bg-muted/60 [&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

/**
 * No hover state, on purpose: row hover is an affordance DataTable applies only
 * to rows that open a record (`onOpenRow`, DESIGN_SYSTEM.md §8.5). A re-sync of
 * shadcn's table must not bring `hover:bg-muted/…` back.
 */
const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('border-b transition-colors data-[state=selected]:bg-muted', className)}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'h-10 whitespace-nowrap px-4 text-left align-middle text-2xs font-semibold uppercase tracking-wider text-muted-foreground [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('px-4 py-3 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
